import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { safetyProgramsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  GraduationCap,
  Plus,
  Pencil,
  Trash2,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Shield,
  Flame,
  Heart,
  UserCog,
  Briefcase,
  Users,
  BookOpen,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const CATEGORY_META = {
  generale: { label: 'Generale', icon: BookOpen, color: 'bg-blue-100 text-blue-800' },
  specifica: { label: 'Specifica', icon: Shield, color: 'bg-emerald-100 text-emerald-800' },
  antincendio: { label: 'Antincendio', icon: Flame, color: 'bg-red-100 text-red-800' },
  primo_soccorso: { label: 'Primo Soccorso', icon: Heart, color: 'bg-pink-100 text-pink-800' },
  preposti: { label: 'Preposti', icon: UserCog, color: 'bg-amber-100 text-amber-800' },
  dirigenti: { label: 'Dirigenti', icon: Briefcase, color: 'bg-purple-100 text-purple-800' },
  rls: { label: 'RLS', icon: Users, color: 'bg-cyan-100 text-cyan-800' },
  altro: { label: 'Altro', icon: GraduationCap, color: 'bg-gray-100 text-gray-800' },
};

const RISCHIO_LABELS = {
  basso: { label: 'Basso', color: 'bg-emerald-100 text-emerald-800' },
  medio: { label: 'Medio', color: 'bg-yellow-100 text-yellow-800' },
  alto: { label: 'Alto', color: 'bg-red-100 text-red-800' },
};

const emptyModulo = () => ({
  titolo: '',
  ore: 1,
  argomenti: [''],
  verifica_apprendimento: '',
});

const emptyProgram = {
  name: '',
  code: '',
  category: 'specifica',
  rischio_livello: '',
  ore_totali: 4,
  normativa: '',
  destinatari: '',
  moduli: [emptyModulo()],
};

export default function SafetyProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyProgram);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await safetyProgramsApi.getAll();
      setPrograms(r.data);
    } catch (e) {
      toast.error('Errore caricamento programmi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(emptyProgram);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name || '',
      code: p.code || '',
      category: p.category || 'specifica',
      rischio_livello: p.rischio_livello || '',
      ore_totali: p.ore_totali || 4,
      normativa: p.normativa || '',
      destinatari: p.destinatari || '',
      moduli: p.moduli && p.moduli.length > 0 ? p.moduli : [emptyModulo()],
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name || !form.code) {
      toast.error('Nome e codice obbligatori');
      return;
    }
    try {
      const payload = {
        ...form,
        rischio_livello: form.rischio_livello || null,
        ore_totali: parseFloat(form.ore_totali) || 0,
        moduli: form.moduli.map((m) => ({
          titolo: m.titolo || '',
          ore: parseFloat(m.ore) || 0,
          argomenti: (m.argomenti || []).filter((a) => a && a.trim()),
          verifica_apprendimento: m.verifica_apprendimento || '',
        })),
      };
      if (editingId) {
        await safetyProgramsApi.update(editingId, payload);
        toast.success('Programma aggiornato');
      } else {
        await safetyProgramsApi.create(payload);
        toast.success('Programma creato');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const remove = async (p) => {
    const msg = p.is_builtin
      ? `Disattivare il programma predefinito "${p.name}"?\nPotrai ripristinarlo con "Ripristina predefiniti".`
      : `Eliminare il programma "${p.name}"?\nQuesta azione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      await safetyProgramsApi.delete(p.id);
      toast.success(p.is_builtin ? 'Programma disattivato' : 'Programma eliminato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const restoreBuiltins = async () => {
    if (!window.confirm('Ripristinare i 12 programmi predefiniti?\nI programmi disattivati torneranno attivi.')) return;
    try {
      const r = await safetyProgramsApi.restoreBuiltins();
      toast.success(`Predefiniti ripristinati (${r.data.builtin_count})`);
      load();
    } catch (e) {
      toast.error('Errore ripristino');
    }
  };

  // Module editor helpers
  const updateModulo = (idx, patch) => {
    const next = [...form.moduli];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, moduli: next });
  };
  const addModulo = () => setForm({ ...form, moduli: [...form.moduli, emptyModulo()] });
  const removeModulo = (idx) => {
    const next = form.moduli.filter((_, i) => i !== idx);
    setForm({ ...form, moduli: next.length ? next : [emptyModulo()] });
  };
  const addArgomento = (modIdx) => {
    const next = [...form.moduli];
    next[modIdx] = { ...next[modIdx], argomenti: [...(next[modIdx].argomenti || []), ''] };
    setForm({ ...form, moduli: next });
  };
  const updateArgomento = (modIdx, argIdx, value) => {
    const next = [...form.moduli];
    const args = [...(next[modIdx].argomenti || [])];
    args[argIdx] = value;
    next[modIdx] = { ...next[modIdx], argomenti: args };
    setForm({ ...form, moduli: next });
  };
  const removeArgomento = (modIdx, argIdx) => {
    const next = [...form.moduli];
    const args = (next[modIdx].argomenti || []).filter((_, i) => i !== argIdx);
    next[modIdx] = { ...next[modIdx], argomenti: args.length ? args : [''] };
    setForm({ ...form, moduli: next });
  };

  const programsByCategory = programs.reduce((acc, p) => {
    const c = p.category || 'altro';
    if (!acc[c]) acc[c] = [];
    acc[c].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="safety-programs-page">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
      >
        <div>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-purple-600" />
            <h1 className="text-2xl font-bold">Programmi Sicurezza</h1>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Cataloga e gestisci i programmi formativi sicurezza. Verranno collegati alle sessioni
            per popolare automaticamente i placeholder <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">«Programma_*»</code>
            (titolo, ore, moduli, argomenti, normativa) nei registri OPN.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={restoreBuiltins} data-testid="restore-builtins-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Ripristina predefiniti
          </Button>
          <Button onClick={openCreate} data-testid="new-program-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo programma
          </Button>
        </div>
      </motion.div>

      {loading ? (
        <p className="text-gray-500 italic">Caricamento...</p>
      ) : (
        Object.entries(CATEGORY_META).map(([catKey, meta]) => {
          const items = programsByCategory[catKey] || [];
          if (items.length === 0) return null;
          const CatIcon = meta.icon;
          return (
            <div key={catKey} className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <CatIcon className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold">{meta.label}</h2>
                <Badge className={meta.color}>{items.length}</Badge>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {items.map((p) => {
                  const isOpen = expanded === p.id;
                  const moduli = p.moduli || [];
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`border rounded-lg bg-white shadow-sm ${!p.is_active ? 'opacity-50' : ''}`}
                      data-testid={`program-card-${p.id}`}
                    >
                      <div className="p-4">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900">{p.name}</h3>
                              {p.is_builtin && (
                                <Badge className="bg-gray-100 text-gray-700 text-xs">Predefinito</Badge>
                              )}
                              {p.rischio_livello && RISCHIO_LABELS[p.rischio_livello] && (
                                <Badge className={`text-xs ${RISCHIO_LABELS[p.rischio_livello].color}`}>
                                  Rischio {RISCHIO_LABELS[p.rischio_livello].label}
                                </Badge>
                              )}
                              <Badge className="bg-purple-100 text-purple-800 text-xs font-semibold">
                                {p.ore_totali}h
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              <span className="font-mono">{p.code}</span>
                              {p.destinatari && <span> · {p.destinatari}</span>}
                            </div>
                            {p.normativa && (
                              <p className="text-xs text-gray-600 mt-1.5 italic">
                                {p.normativa.substring(0, 110)}{p.normativa.length > 110 ? '...' : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openEdit(p)}
                              title="Modifica"
                              data-testid={`edit-program-${p.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => remove(p)}
                              title="Elimina"
                              data-testid={`delete-program-${p.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : p.id)}
                          className="mt-3 text-xs flex items-center gap-1 text-purple-700 hover:underline"
                        >
                          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {moduli.length} moduli {isOpen ? '(nascondi)' : '(mostra)'}
                        </button>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-3 space-y-2 border-t pt-3 text-xs"
                          >
                            {moduli.map((m, i) => (
                              <div key={i} className="bg-gray-50 rounded p-2.5">
                                <div className="font-semibold text-gray-800">
                                  MODULO {i + 1} — {m.titolo}
                                  {m.ore && <span className="text-purple-700 font-mono ml-2">({m.ore}h)</span>}
                                </div>
                                {(m.argomenti || []).length > 0 && (
                                  <ul className="list-disc list-inside text-gray-700 mt-1.5 ml-2 space-y-0.5">
                                    {m.argomenti.map((a, j) => (
                                      <li key={j}>{a}</li>
                                    ))}
                                  </ul>
                                )}
                                {m.verifica_apprendimento && (
                                  <div className="text-gray-600 italic mt-1.5">
                                    <strong>Verifica:</strong> {m.verifica_apprendimento}
                                  </div>
                                )}
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica programma' : 'Nuovo programma sicurezza'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nome programma *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="es. Formazione Specifica — Rischio Alto"
                  data-testid="program-name"
                />
              </div>
              <div>
                <Label>Codice *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="es. SPEC_LAV_ALTO_12H"
                  data-testid="program-code"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Livello di rischio</Label>
                <Select
                  value={form.rischio_livello || '__none__'}
                  onValueChange={(v) => setForm({ ...form, rischio_livello: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Non applicabile</SelectItem>
                    <SelectItem value="basso">Basso</SelectItem>
                    <SelectItem value="medio">Medio</SelectItem>
                    <SelectItem value="alto">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ore totali *</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.ore_totali}
                  onChange={(e) => setForm({ ...form, ore_totali: e.target.value })}
                />
              </div>
              <div>
                <Label>Destinatari</Label>
                <Input
                  value={form.destinatari}
                  onChange={(e) => setForm({ ...form, destinatari: e.target.value })}
                  placeholder="es. Tutti i lavoratori a rischio alto"
                />
              </div>
            </div>
            <div>
              <Label>Riferimenti normativi</Label>
              <Textarea
                value={form.normativa}
                onChange={(e) => setForm({ ...form, normativa: e.target.value })}
                rows={2}
                placeholder="D.Lgs. 81/2008 art. 37 — Accordo Stato-Regioni 21/12/2011 ..."
              />
            </div>

            {/* Moduli editor */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Moduli didattici</Label>
                <Button type="button" size="sm" variant="outline" onClick={addModulo}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi modulo
                </Button>
              </div>
              <div className="space-y-3">
                {form.moduli.map((m, mIdx) => (
                  <div key={mIdx} className="border rounded-lg p-3 bg-purple-50/30">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-xs font-semibold text-purple-700 bg-purple-100 rounded px-2 py-0.5 mt-2">
                        M{mIdx + 1}
                      </span>
                      <Input
                        value={m.titolo}
                        onChange={(e) => updateModulo(mIdx, { titolo: e.target.value })}
                        placeholder="Titolo modulo"
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        step="0.5"
                        value={m.ore}
                        onChange={(e) => updateModulo(mIdx, { ore: e.target.value })}
                        placeholder="ore"
                        className="w-20"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeModulo(mIdx)}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="ml-8 space-y-1.5">
                      <Label className="text-xs">Sub-argomenti</Label>
                      {(m.argomenti || []).map((a, aIdx) => (
                        <div key={aIdx} className="flex gap-1.5">
                          <Input
                            value={a}
                            onChange={(e) => updateArgomento(mIdx, aIdx, e.target.value)}
                            placeholder="Argomento..."
                            className="text-sm flex-1"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeArgomento(mIdx, aIdx)}
                            className="text-red-500"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => addArgomento(mIdx)}
                        className="text-purple-700"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Aggiungi argomento
                      </Button>
                      <div className="pt-2">
                        <Label className="text-xs">Verifica apprendimento</Label>
                        <Input
                          value={m.verifica_apprendimento}
                          onChange={(e) => updateModulo(mIdx, { verifica_apprendimento: e.target.value })}
                          placeholder="es. Test finale 20 domande"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-program-btn">
              {editingId ? 'Salva modifiche' : 'Crea programma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
